import http from '@/http';
import { API } from '@/constants/api';
import { parse } from '@/utils';
import { camelize, snakelize } from '@/utils/parse';
import { SENDER_CONSTANT } from '@/constants';
import { generatePath } from 'react-router-dom';
import { alertError, alertSuccess } from '@/utils/notification';

export async function validateData({ filePath, templateId, senderId }) {
  try {
    let payload = camelize({
      filename: filePath,
      sender: senderId,
    });

    if (templateId) {
      payload = {
        ...payload,
        template: templateId,
      };
    }

    const { status, data } = await http.post(
      API.SMS_MANAGERMENT.SEND_USING_FILE.VALIDATE,
      payload,
    );

    if (status === 200) {
      return {
        success: true,
        data: parse.camelize(data),
      };
    }
    return {
      success: false,
    };
  } catch (error) {
    console.error(error);

    if (error.response) {
      const { errorCode } = parse.camelize(error.response.data);
      alertError(errorCode);
    } else {
      alertError('APPLICATION_ERROR');
    }
    return {
      success: false,
    };
  }
}

export async function createBatch({
  filePath,
  templateId,
  senderId,
  description,
  scheduleAt,
}) {
  try {
    let payload = camelize({
      filename: filePath,
      sender: senderId,
      description,
    });

    if (templateId) {
      payload = {
        ...payload,
        template: templateId,
      };
    }

    if (scheduleAt) {
      payload = {
        ...payload,
        scheduleAt: scheduleAt.format('YYYY-MM-DD HH:mm'),
      };
    }

    const { status, data } = await http.post(
      API.SMS_MANAGERMENT.SEND_USING_FILE.CREATE,
      parse.snakelize(payload),
    );

    if (status === 201) {
      return {
        success: true,
        data: parse.camelize(data),
      };
    }
    return {
      success: false,
    };
  } catch (error) {
    console.error(error);

    if (error.response) {
      const { errorCode } = parse.camelize(error.response.data);
      alertError(errorCode);
    } else {
      alertError('APPLICATION_ERROR');
    }
    return {
      success: false,
    };
  }
}

export async function createSender({
  senderName,
  useType,
  companyName,
  websiteLink,
  registrationDocument,
  description,
}) {
  try {
    const formData = new FormData();
    formData.append('sender_name', senderName);
    formData.append('use_type', useType);
    formData.append('company_name', companyName);
    formData.append('description', description);

    if (websiteLink) {
      formData.append('website_link', websiteLink);
    }

    if (registrationDocument) {
      formData.append('registration_document', registrationDocument);
    }

    const config = {
      headers: { 'content-type': 'multipart/form-data' },
    };

    const { status, data } = await http.post(
      API.SMS_MANAGERMENT.SENDER.CREATE,
      formData,
      config,
    );

    if (status === 201) {
      alertSuccess('CREATE_SENDER_SUCCESS');

      return {
        success: true,
        data: parse.camelize(data),
      };
    }

    return {
      success: false,
    };
  } catch (error) {
    if (error.response) {
      const { errorCode } = parse.camelize(error.response.data);
      alertError(errorCode);
    } else {
      alertError('APPLICATION_ERROR');
    }
    return {
      success: false,
    };
  }
}

export async function fetchActiveSenderList({ name }) {
  try {
    const params = snakelize({
      status: SENDER_CONSTANT.STATUSES.find((item) => item.label === 'Active')
        ?.value,
      senderName: name,
      pageSize: 100,
    });

    const resp = await http.get(API.SMS_MANAGERMENT.SENDER.INDEX, { params });

    if (resp.status === 200) {
      return {
        success: true,
        data: camelize(resp.data.results),
      };
    }
    return {
      success: false,
      data: null,
    };
  } catch (error) {
    console.log('error', error);
    return {
      success: false,
      data: null,
    };
  }
}

export async function fetchActiveAndBlockSenderList({ name }) {
  try {
    const params = snakelize({
      senderName: name,
      pageSize: 100,
    });

    const respActivePromise = http.get(API.SMS_MANAGERMENT.SENDER.INDEX, {
      params: {
        ...params,
        status: SENDER_CONSTANT.STATUSES.find((item) => item.label === 'Active')
          ?.value,
      },
    });
    const respBlockPromise = http.get(API.SMS_MANAGERMENT.SENDER.INDEX, {
      params: {
        ...params,
        status: SENDER_CONSTANT.STATUSES.find(
          (item) => item.label === 'Blocked',
        )?.value,
      },
    });
    const [respActive, respBlock] = await Promise.all([
      respActivePromise,
      respBlockPromise,
    ]);

    if (respActive.status !== 200 && respBlock !== 200) {
      return {
        success: false,
        data: null,
      };
    }

    const data = [];
    if (respActive.status === 200) {
      data.push(...respActive.data.results);
    }
    if (respBlock.status === 200) {
      data.push(...respBlock.data.results);
    }

    return {
      success: true,
      data: camelize(data),
    };
  } catch (error) {
    console.log('error', error);
    return {
      success: false,
      data: null,
    };
  }
}

export async function createSmsTemplate({ name, content }) {
  try {
    const payload = snakelize({
      name,
      content,
    });
    const { status, data } = await http.post(
      API.SMS_MANAGERMENT.SMS_TEMPLATE.CREATE,
      payload,
    );

    if (status === 201) {
      alertSuccess('CREATE_SMS_TEMPLATE_SUCCESS');

      return {
        success: true,
        data: parse.camelize(data),
      };
    }

    return {
      success: false,
    };
  } catch (error) {
    if (error.response) {
      const { errorCode } = parse.camelize(error.response.data);
      alertError(errorCode);
    } else {
      alertError('APPLICATION_ERROR');
    }
    return {
      success: false,
    };
  }
}

export async function updateSmsTemplate({ id, name, content, isActive }) {
  try {
    const payload = snakelize({
      name,
      content,
      isActive,
    });
    const url = generatePath(API.SMS_MANAGERMENT.SMS_TEMPLATE.UPDATE, { id });
    const { status, data } = await http.patch(url, payload);

    if (status === 200) {
      alertSuccess('UPDATE_SMS_TEMPLATE_SUCCESS');
      return {
        success: true,
        data: parse.camelize(data),
      };
    }

    return {
      success: false,
    };
  } catch (error) {
    if (error.response) {
      const { errorCode } = parse.camelize(error.response.data);
      alertError(errorCode);
    } else {
      alertError('APPLICATION_ERROR');
    }
    return {
      success: false,
    };
  }
}

export async function fetchActiveSmsTemplateList() {
  try {
    const params = snakelize({
      isActive: true,
    });

    const resp = await http.get(API.SMS_MANAGERMENT.SMS_TEMPLATE.INDEX, {
      params,
    });

    if (resp.status === 200) {
      return {
        success: true,
        data: camelize(resp.data.results),
      };
    }
    return {
      success: false,
      data: null,
    };
  } catch (error) {
    console.log('error', error);
    return {
      success: false,
      data: null,
    };
  }
}

export async function sendSms({ toPhoneNumber, senderId, content }) {
  try {
    const payload = snakelize({
      toPhoneNumber,
      content,
      sender: senderId,
    });
    const { status, data } = await http.post(
      API.SMS_MANAGERMENT.SEND_SMS,
      payload,
    );

    if (status === 201) {
      alertSuccess('SEND_SMS_SUCCESS');
      return {
        success: true,
        data: parse.camelize(data),
      };
    }

    return {
      success: false,
    };
  } catch (error) {
    if (error.response) {
      const { errorCode } = parse.camelize(error.response.data);
      alertError(errorCode);
    } else {
      alertError('APPLICATION_ERROR');
    }
    return {
      success: false,
    };
  }
}
